import React from 'react';
import {ElementsConsumer, CardElement} from '@stripe/react-stripe-js';

import CardSection from './CardSection';
import AppUrl from '../../restApi/AppUrl';
import IRestClient from '../../restApi/IRestClient';

class CheckoutForm extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      isProcessing: false,
      error: null
    };
  }

  handleSubmit = async (event) => {
    // We don't want to let default form submission happen here,
    // which would refresh the page.
    event.preventDefault();

    const {stripe, elements} = this.props

    if (!stripe || !elements) {
      // Stripe.js hasn't yet loaded.
      // Make  sure to disable form submission until Stripe.js has loaded.
      return;
    }

    // Get the selected amount from parent component
    const { selectedAmount, customAmount } = this.props;
    let amountToCharge = 0;

    console.log('🔍 Debug - Props received:', {
      selectedAmount,
      customAmount,
      name: this.props.name,
      email: this.props.email
    });

    if (selectedAmount) {
      amountToCharge = selectedAmount;
      console.log('💰 Using selected amount:', amountToCharge);
    } else if (customAmount && customAmount > 0) {
      amountToCharge = parseFloat(customAmount);
      console.log('💰 Using custom amount:', amountToCharge);
    } else {
      console.log('❌ No valid amount found');
      this.setState({ 
        error: 'Please select a donation amount or enter a custom amount', 
        isProcessing: false 
      });
      return;
    }

    console.log('💳 Amount to charge:', amountToCharge);

    this.setState({ isProcessing: true, error: null });

    const card = elements.getElement(CardElement);
    const result = await stripe.createToken(card);
    console.log('🎫 Stripe token result:', result);

    if (result.error) {
      // Show error to your customer.
      console.log('❌ Stripe error:', result.error.message);
      this.setState({ 
        error: result.error.message, 
        isProcessing: false 
      });
    } else {
      // Send the token to your server.
      console.log('✅ Token created successfully, sending to backend...');
      this.stripeTokenHandler(result.token, amountToCharge);
    }
  };
 
  // Method to handle the token submission to the server
  stripeTokenHandler = async (token, amount) => {
    try {
      // Prepare the payment data to send to the backend
      const paymentData = { 
        token: token.id,
        amount: amount,
        name: this.props.name,
        email: this.props.email
      };
  
      // Call the PostRequest method from IRestClient to send the token to the server
      const response = await IRestClient.PostRequest(AppUrl.donate, paymentData); // No need for JSON.stringify here
      console.log('Server Response:', response);

      // Handle the server's response
      if (response && response.status === 'success') {
        this.setState({ isProcessing: false });
        
        // Store donation info in localStorage for the thank you page
        localStorage.setItem('lastDonationAmount', amount);
        localStorage.setItem('lastDonationName', this.props.name);
        localStorage.setItem('lastDonationEmail', this.props.email);
        localStorage.setItem('lastDonationId', response.donation_id);
        
        // Redirect to thank you page
        window.location.href = `/thank-you?amount=${amount}&name=${encodeURIComponent(this.props.name)}&email=${encodeURIComponent(this.props.email)}&donation_id=${response.donation_id}`;
      } else {
        this.setState({ 
          error: response.message || 'An unknown error occurred', 
          isProcessing: false 
        });
      }
    } catch (error) {
      // Handle any network or server errors
      console.error('Error submitting token:', error);
      this.setState({ 
        error: 'There was an error processing your donation. Please try again.', 
        isProcessing: false 
      });
    }
  };
  
  render() {
    const { isProcessing, error } = this.state;

    return (
      <form onSubmit={this.handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
        <CardSection />
        
        {error && (
          <div style={{
            backgroundColor: '#fef2f2',
            border: '1px solid #fecaca',
            borderRadius: '0.5rem',
            padding: '0.75rem'
          }}>
            <p style={{ color: '#dc2626', fontSize: '0.875rem' }}>{error}</p>
          </div>
        )}
        
        <button 
          type="submit" 
          disabled={!this.props.stripe || isProcessing}
          style={{
            width: '100%',
            padding: 'clamp(0.625rem, 2vw, 0.75rem) clamp(0.75rem, 2.5vw, 1rem)',
            borderRadius: '0.5rem',
            fontWeight: '600',
            transition: 'all 0.2s ease',
            border: 'none',
            cursor: isProcessing || !this.props.stripe ? 'not-allowed' : 'pointer',
            fontSize: 'clamp(0.75rem, 2.5vw, 0.875rem)',
            minHeight: 'clamp(2.5rem, 6vw, 3rem)',
            ...(isProcessing || !this.props.stripe
              ? {
                  backgroundColor: '#d1d5db',
                  color: '#6b7280'
                }
              : {
                  background: 'linear-gradient(to right, #10b981, #059669)',
                  color: 'white'
                }
            )
          }}
          onMouseEnter={(e) => {
            if (!isProcessing && this.props.stripe) {
              e.target.style.background = 'linear-gradient(to right, #059669, #047857)';
              e.target.style.boxShadow = '0 10px 15px -3px rgba(0, 0, 0, 0.1)';
              e.target.style.transform = 'scale(1.02)';
            }
          }}
          onMouseLeave={(e) => {
            if (!isProcessing && this.props.stripe) {
              e.target.style.background = 'linear-gradient(to right, #10b981, #059669)';
              e.target.style.boxShadow = 'none';
              e.target.style.transform = 'scale(1)';
            }
          }}
        >
          {isProcessing ? (
            <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <svg style={{ 
                animation: 'spin 1s linear infinite',
                marginRight: '0.75rem',
                height: '1.25rem',
                width: '1.25rem',
                color: 'white'
              }} xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle style={{ opacity: '0.25' }} cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path style={{ opacity: '0.75' }} fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              Processing...
            </span>
          ) : (
            <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <svg style={{ width: '1.25rem', height: '1.25rem', marginRight: '0.5rem' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              Confirm Payment
            </span>
          )}
        </button>
      </form>
    );
  }
}

export default function InjectedCheckoutForm(props) {
  return (
    <ElementsConsumer>
      {({stripe, elements}) => (
        <CheckoutForm 
          stripe={stripe} 
          elements={elements}
          selectedAmount={props.selectedAmount}
          customAmount={props.customAmount}
          name={props.name}
          email={props.email}
        />
      )}
    </ElementsConsumer>
  );
}